'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { formatRelative } from 'date-fns';
import { MessageSquare, Repeat2, Send, ThumbsUp, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import showdown from 'showdown';

import { CarouselPost } from '@kit/react-carousel';
import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Button } from '@kit/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@kit/ui/card';
import { cn } from '@kit/ui/utils';

import mediaStyles from './linkedin-video.module.css';

const converter = new showdown.Converter({
  extensions: [
    {
      type: 'output',
      regex: /#(\w+)/g,
      replace: (match, hashtag) => {
        const url = `https://www.linkedin.com/feed/hashtag/?keywords=${encodeURIComponent(hashtag.toLowerCase())}`;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">#${hashtag}</a>`;
      },
    },
  ],
  simpleLineBreaks: true,
  strikethrough: true,
  tables: true,
  tasklists: true,
  ghCodeBlocks: true,
  smoothLivePreview: true,
  smartIndentationFix: true,
  disableForced4SpacesIndentedSublists: true,
  ghCompatibleHeaderId: true,
  openLinksInNewWindow: true,
  emoji: true,
  simplifiedAutoLink: true,
  requireSpaceBeforeHeadingText: true,
});

// Higher-order component for the post header
const PostHeader = ({ author, timestamp, postUrl }) => {
  const [avatarSrc, setAvatarSrc] = useState(author.avatar);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setAvatarSrc(URL.createObjectURL(file));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
  });

  return (
    <CardHeader className="flex w-full flex-row items-start gap-3 overflow-hidden pb-2">
      <div {...getRootProps()} className="relative cursor-pointer">
        <Avatar className="h-12 w-12">
          <AvatarImage src={avatarSrc} alt={author.name} />
          <AvatarFallback>{author.name.charAt(0)}</AvatarFallback>
        </Avatar>
        <input {...getInputProps()} />
        {isDragActive && (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-50">
            <p className="text-center text-xs text-white">Drop</p>
          </div>
        )}
      </div>
      <div className="flex w-full flex-col overflow-hidden">
        {author.profileUrl ? (
          <>
            <a
              href={author.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <h3 className="whitespace-pre-wrap text-sm font-semibold outline-none">
                {author.name}
              </h3>
            </a>
            {author.headline && (
              <div className="text-card-foreground/70 overflow-hidden text-ellipsis text-nowrap text-xs outline-none">
                {author.headline}
              </div>
            )}
          </>
        ) : (
          <>
            <h3
              className="whitespace-pre-wrap text-sm font-semibold outline-none"
              contentEditable
            >
              {author.name}
            </h3>
            {author.headline && (
              <div
                className="text-card-foreground/70 overflow-hidden text-ellipsis text-nowrap text-xs outline-none"
                contentEditable
              >
                {author.headline}
              </div>
            )}
          </>
        )}
        {postUrl ? (
          <a href={postUrl} target="_blank" rel="noopener noreferrer">
            <div className="text-card-foreground/70 mt-1 whitespace-pre-wrap text-xs capitalize outline-none">
              {formatRelative(timestamp, new Date())}
            </div>
          </a>
        ) : timestamp ? (
          <div
            className="text-card-foreground/70 mt-1 whitespace-pre-wrap text-xs capitalize outline-none"
            contentEditable
          >
            {formatRelative(timestamp, new Date())}
          </div>
        ) : null}
      </div>
    </CardHeader>
  );
};

// Add this new component for reshared posts
const ResharedPost = ({ resharedPost }) => {
  const { text, author, article, image, video, document } = resharedPost;

  const postData = {
    author: {
      name: `${author.firstName} ${author.lastName}`,
      headline: author.headline,
      profileUrl: author.url,
    },
    content: text,
    article,
    images: image,
    videos: video,
    document,
    readOnly: true,
  };

  return (
    <div className="bg-background/5 overflow-hidden rounded-xl border">
      <EnhancedPost {...postData} />
    </div>
  );
};

// Higher-order component for the post content
const PostContent = ({
  postUrl,
  content,
  images,
  videos,
  article,
  document,
  company,
  title,
  reposted,
  readOnly,
  resharedPost,
}) => {
  const [postImages, setPostImages] = useState(images);

  const handleImagesChange = useCallback((newImages: string[]) => {
    setPostImages(newImages);
    // You might want to do something else here, like update a form state or send to an API
  }, []);

  const markdownToHtml = (markdown: string): string => {
    return converter.makeHtml(markdown);
  };

  const contentRef = useRef<HTMLDivElement>(null);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setPostImages([URL.createObjectURL(file)]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    noClick: true,
  });

  const styleURLs = useCallback((node: Node) => {
    if (node.nodeType === Node.TEXT_NODE && typeof window !== 'undefined') {
      const regex = /(https?:\/\/[^\s]+)|(#[\w\d-]+)|(@[\w\d-]+)/g;
      const text = node.textContent || '';
      const matches = text.match(regex);

      if (matches?.length > 0 && document?.createDocumentFragment) {
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        matches.forEach((match) => {
          const index = text.indexOf(match, lastIndex);
          if (index > lastIndex) {
            fragment.appendChild(
              document.createTextNode(text.slice(lastIndex, index)),
            );
          }
          const span = document.createElement('span');
          span.textContent = match;
          if (match.startsWith('http')) {
            span.className = 'text-blue-500 cursor-pointer font-semibold';
          } else if (match.startsWith('#')) {
            span.className = 'text-blue-500 cursor-pointer font-semibold';
          } else if (match.startsWith('@')) {
            span.className = 'text-blue-500 cursor-pointer font-semibold';
          }
          fragment.appendChild(span);
          lastIndex = index + match.length;
        });

        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        node.parentNode?.replaceChild(fragment, node);
      }
    } else if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.nodeName !== 'SPAN'
    ) {
      Array.from(node.childNodes).forEach(styleURLs);
    }
  }, []);

  const handleClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'SPAN') {
      const text = target.textContent || '';
      if (text.startsWith('http')) {
        window.open(text, '_blank');
      } else if (text.startsWith('#')) {
        window.open(
          `https://www.linkedin.com/feed/hashtag/?keywords=${encodeURIComponent(text.slice(1))}`,
          '_blank',
        );
      } else if (text.startsWith('@')) {
        window.open(
          `https://www.linkedin.com/in/${encodeURIComponent(text.slice(1))}`,
          '_blank',
        );
      }
      e.preventDefault();
    }
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const sanitizedText = text.replace(/\n/g, '<br>');
    document.execCommand('insertHTML', false, sanitizedText);
  }, []);

  const handleInput = useCallback(() => {
    if (!contentRef.current) return;

    const selection = window.getSelection();
    let range: Range | null = null;
    let caretOffset = 0;

    if (selection && selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
      const preCaretRange = range.cloneRange();
      preCaretRange.selectNodeContents(contentRef.current);
      preCaretRange.setEnd(range.endContainer, range.endOffset);
      caretOffset = getNodeOffset(
        contentRef.current,
        preCaretRange.endContainer,
        preCaretRange.endOffset,
      );
    }

    // Apply URL styling
    Array.from(contentRef.current.childNodes).forEach(styleURLs);

    // Restore the cursor position
    if (range) {
      try {
        const [newNode, newOffset] = getNodeAndOffsetAtCharacterOffset(
          contentRef.current,
          caretOffset,
        );
        if (newNode) {
          const maxOffset = getMaxOffset(newNode);
          const safeOffset = Math.min(newOffset, maxOffset);
          range.setStart(newNode, safeOffset);
          range.setEnd(newNode, safeOffset);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      } catch (error) {
        console.warn('Error setting cursor position:', error);
      }
    }
  }, [styleURLs]);

  const getMaxOffset = (node: Node): number => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent?.length || 0;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      return node.childNodes.length;
    }
    return 0;
  };

  const getNodeAndOffsetAtCharacterOffset = (
    root: Node,
    targetOffset: number,
  ): [Node | null, number] => {
    try {
      let currentOffset = 0;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_ALL, null);

      while (walker.nextNode()) {
        const nodeLength =
          walker.currentNode.nodeType === Node.TEXT_NODE
            ? walker.currentNode.textContent?.length || 0
            : walker.currentNode.nodeName === 'BR'
              ? 1
              : 0;

        if (currentOffset + nodeLength > targetOffset) {
          if (walker.currentNode.nodeType === Node.TEXT_NODE) {
            return [walker.currentNode, targetOffset - currentOffset];
          } else if (walker.currentNode.nodeName === 'BR') {
            const nextNode = walker.nextNode();
            return nextNode
              ? [nextNode, 0]
              : [
                  walker.currentNode.parentNode,
                  walker.currentNode.parentNode?.childNodes.length || 0,
                ];
          }
        }
        currentOffset += nodeLength;
      }
      return [root.lastChild, getMaxOffset(root.lastChild || root)];
    } catch (error) {
      // console.warn('Error getting node and offset:', error);
      return [null, 0];
    }
  };

  const getNodeOffset = (root: Node, node: Node, offset: number): number => {
    try {
      let currentOffset = 0;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_ALL, null);

      while (walker.nextNode()) {
        if (walker.currentNode === node) {
          return currentOffset + offset;
        }
        if (walker.currentNode.nodeType === Node.TEXT_NODE) {
          currentOffset += walker.currentNode.textContent?.length || 0;
        } else if (walker.currentNode.nodeName === 'BR') {
          currentOffset += 1;
        }
      }
      return currentOffset;
    } catch (error) {
      // console.warn('Error getting node offset:', error);
      return 0;
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.innerHTML = markdownToHtml(content);
      handleInput();
      contentRef.current.addEventListener('click', handleClick);
    }

    return () => {
      contentRef.current?.removeEventListener('click', handleClick);
    };
  }, [content, handleInput, handleClick]);

  return (
    <CardContent className="mt-4 pt-0 lg:min-w-[575px]">
      <div
        ref={contentRef}
        contentEditable={!readOnly}
        className="[&_a]:text-primary mb-4 whitespace-pre-wrap outline-none"
        onInput={handleInput}
        onPaste={handlePaste}
      />
      {resharedPost ? (
        <ResharedPost resharedPost={resharedPost} />
      ) : reposted ? (
        <a href={postUrl} target="_blank" rel="noopener noreferrer">
          <div className="btn relative mx-auto flex h-[200px] w-[400px] items-center justify-center rounded-xl border-0 bg-slate-100">
            <span className="text-sm text-slate-500">Shared Post</span>
          </div>
        </a>
      ) : postImages && postImages.length > 1 ? (
        <CarouselPost
          images={postImages}
          title={title}
          type="linkedin"
          onImagesChange={handleImagesChange}
        />
      ) : (
        <div
          {...getRootProps()}
          className={cn('relative', !readOnly && 'min-h-[60px]')}
        >
          {postImages?.[0] ? (
            <>
              <img
                src={postImages[0].url || postImages[0]}
                alt="Post image"
                className="w-full rounded-md"
              />
              {!readOnly && (
                <button
                  onClick={() => setPostImages([])}
                  className="absolute right-2 top-2 rounded-full bg-black bg-opacity-50 p-1"
                >
                  <X className="h-4 w-4 text-white" />
                </button>
              )}
            </>
          ) : videos?.[0] ? (
            <div className="relative h-fit w-full">
              <PostVideo
                posterImage={videos[0].poster}
                videoUrl={videos[0].url}
              />
            </div>
          ) : article?.link ? (
            <div className="relative h-fit w-full">
              <PostLinkPreview article={article} images={images} />
            </div>
          ) : document?.title ? (
            <div className="relative h-fit w-full">
              <CarouselPost
                images={document.coverPages?.[1].imageUrls}
                title={document.title}
                type="linkedin"
              />
            </div>
          ) : company?.title ? (
            <div className="relative h-fit w-full" />
          ) : null}
          <input {...getInputProps()} />
          {!readOnly && isDragActive && (
            <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/30">
              <p className="text-center text-white">
                Drop the image here to replace
              </p>
            </div>
          )}
          {!readOnly &&
            !postImages?.[0] &&
            !videos?.[0] &&
            !article &&
            !isDragActive && (
              <div className="rounded-md border-2 border-dashed border-gray-300 p-4 text-center text-gray-300 dark:border-gray-600 dark:text-gray-600">
                Drag and drop an image here, or click to select a file
              </div>
            )}
        </div>
      )}
    </CardContent>
  );
};

const PostVideo = ({ posterImage, videoUrl }: Props) => {
  return (
    <video
      className={mediaStyles.image}
      poster={posterImage}
      controls
      preload="auto"
    >
      <source src={videoUrl} type="video/mp4" />
    </video>
  );
};

const PostLinkPreview = ({ article }) => {
  const { title, subtitle, link, open_graph, image, smallImage } = article;

  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      <div className="flex min-h-[100px] items-center overflow-hidden rounded-md border">
        {(smallImage?.[0]?.url ||
          open_graph?.images?.length > 0 ||
          image?.length > 0) && (
          <div className="relative h-fit w-1/3 flex-shrink-0">
            <img
              src={
                smallImage?.[0]?.url || open_graph?.images?.[0]?.url || image
              }
              alt={title || 'Link preview'}
            />
          </div>
        )}
        <div className="ml-2 flex-1 p-2">
          <h3 className="line-clamp-2 text-sm font-semibold">{title}</h3>
          <p className="mt-1 line-clamp-1 text-xs">{subtitle}</p>
        </div>
      </div>
    </a>
  );
};

// Higher-order component for the post reactions
const PostReactions = ({ reactions, comments, shares, postUrl }) => (
  <a href={postUrl} target="_blank" rel="noopener noreferrer">
    <div className="flex items-center justify-between px-4 py-2 text-xs text-gray-500">
      <div className="flex items-center">
        <ThumbsUp className="mr-1 h-3 w-3 text-blue-500" />
        <span>{reactions || 0}</span>
      </div>
      <div className="flex items-center">
        <button className="hover:underline">{comments || 0} comments</button>
        {shares && (
          <>
            <span className="mx-2">•</span>
            <button className="hover:underline">{shares || 0} reposts</button>
          </>
        )}
      </div>
    </div>
  </a>
);

// Higher-order component for the post actions
const PostActions = ({ postUrl }: { postUrl: string }) => (
  <CardFooter className="flex justify-evenly border-t pt-2">
    {[
      { icon: ThumbsUp, label: 'Like' },
      { icon: MessageSquare, label: 'Comment' },
      { icon: Repeat2, label: 'Repost' },
      { icon: Send, label: 'Send' },
    ].map(({ icon: Icon, label }, index) => (
      <a href={postUrl} target="_blank" rel="noopener noreferrer" key={index}>
        <Button
          key={label}
          variant="ghost"
          size="icon"
          className="text-foreground hover:text-primary mt-2 flex flex-col items-center gap-1 rounded-md text-xs"
        >
          <Icon className="h-4 w-4" />
        </Button>
      </a>
    ))}
  </CardFooter>
);

// Compose the EnhancedPost component
const EnhancedPost = ({
  author,
  timestamp,
  content,
  images,
  videos,
  article,
  document,
  company,
  title,
  reposted,
  reactions,
  shares,
  comments,
  postUrl,
  readOnly,
  resharedPost,
}) => (
  <>
    <PostHeader author={author} timestamp={timestamp} postUrl={postUrl} />
    <PostContent
      content={content}
      images={images}
      videos={videos}
      article={article}
      document={document}
      company={company}
      title={title}
      reposted={reposted}
      postUrl={postUrl}
      readOnly={readOnly}
      resharedPost={resharedPost}
    />
    {!resharedPost && (
      <>
        <PostReactions
          reactions={reactions}
          shares={shares}
          comments={comments}
          postUrl={postUrl}
        />
        <PostActions postUrl={postUrl} />
      </>
    )}
  </>
);

// Main component
export default function LinkedInPost({ postData, readOnly }: any) {
  return (
    <Card className="mx-auto max-w-xl">
      <EnhancedPost {...postData} readOnly={readOnly} />
    </Card>
  );
}
